// src/components/sections/TestimonialsSection.js
import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card } from 'react-bootstrap';
import { motion, AnimatePresence } from 'framer-motion';
import { FaQuoteLeft, FaStar, FaGoogle, FaPlayCircle } from 'react-icons/fa';

const TestimonialsSection = () => {
    const [activeIndex, setActiveIndex] = useState(0);

    const testimonials = [
        {
            name: "Sarah Johnson",
            role: "New Mother",
            rating: 5,
            content: "The care I received during my pregnancy and delivery was exceptional. The staff was compassionate, professional, and made me feel completely safe throughout the entire process.",
            date: "2 weeks ago",
            image: "https://images.pexels.com/photos/774909/pexels-photo-774909.jpeg?auto=compress&cs=tinysrgb&w=400&h=400&fit=crop&crop=face"
        },
        {
            name: "Michael Chen",
            role: "Father of NICU Baby",
            rating: 5,
            content: "When our baby was born premature, the NICU team saved his life. Their expertise and support during those difficult weeks were incredible. We're forever grateful.",
            date: "1 month ago",
            image: "https://images.pexels.com/photos/2379004/pexels-photo-2379004.jpeg?auto=compress&cs=tinysrgb&w=400&h=400&fit=crop&crop=face"
        },
        {
            name: "Priya Sharma",
            role: "IVF Patient",
            rating: 5,
            content: "After years of trying, we finally have our miracle baby thanks to the fertility specialists. Their personalized approach and emotional support made all the difference.",
            date: "3 months ago",
            image: "https://images.pexels.com/photos/1181686/pexels-photo-1181686.jpeg?auto=compress&cs=tinysrgb&w=400&h=400&fit=crop&crop=face"
        },
        {
            name: "Robert Williams",
            role: "Pediatric Surgery Parent",
            rating: 5,
            content: "The pediatric surgery team was amazing with our son. They explained everything clearly, kept us updated, and their skill gave us our healthy, active child back.",
            date: "2 months ago",
            image: "https://images.pexels.com/photos/1222271/pexels-photo-1222271.jpeg?auto=compress&cs=tinysrgb&w=400&h=400&fit=crop&crop=face"
        },
        {
            name: "Emily Rodriguez",
            role: "High-Risk Pregnancy Patient",
            rating: 5,
            content: "Being a high-risk pregnancy was scary, but the specialist team monitored me closely and provided exceptional care. Healthy baby delivered at 38 weeks!",
            date: "4 months ago",
            image: "https://images.pexels.com/photos/774095/pexels-photo-774095.jpeg?auto=compress&cs=tinysrgb&w=400&h=400&fit=crop&crop=face"
        },
        {
            name: "David Kim",
            role: "Emergency Care Patient",
            rating: 5,
            content: "When we rushed in with our child having breathing difficulties, the emergency team acted immediately and professionally. Outstanding care at 3 AM!",
            date: "1 week ago",
            image: "https://images.pexels.com/photos/2379005/pexels-photo-2379005.jpeg?auto=compress&cs=tinysrgb&w=400&h=400&fit=crop&crop=face"
        }
    ];

    const googleRating = {
        rating: 4.9,
        reviews: "1,234",
        stars: 5
    };

    // Auto-rotate carousel effect
    useEffect(() => {
        const interval = setInterval(() => {
            setActiveIndex((prevIndex) =>
                prevIndex === testimonials.length - 1 ? 0 : prevIndex + 1
            );
        }, 5000); // Rotate every 5 seconds

        return () => clearInterval(interval);
    }, [testimonials.length]);

    return (
        <section id="testimonials" className="section-padding" style={{ backgroundColor: 'var(--light-bg)' }}>
            <Container>
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.8 }}
                    viewport={{ once: true }}
                    className="text-center mb-5"
                >
                    <h2 className="display-5 fw-bold mb-3" style={{ color: 'var(--dark-teal)' }}>
                        Patient Stories & Testimonials
                    </h2>
                    <p className="lead" style={{ color: '#666', maxWidth: '700px', margin: '0 auto' }}>
                        Hear from families who have experienced our compassionate care firsthand
                    </p>
                </motion.div>

                {/* Google Rating Box */}
                <motion.div
                    initial={{ opacity: 0, scale: 0.9 }}
                    whileInView={{ opacity: 1, scale: 1 }}
                    transition={{ duration: 0.5 }}
                    viewport={{ once: true }}
                    className="mb-5"
                >
                    <Card className="border-0 healthcare-card" style={{ maxWidth: '400px', margin: '0 auto' }}>
                        <Card.Body className="p-4">
                            <div className="d-flex align-items-center mb-3">
                                <FaGoogle size={24} className="me-3" style={{ color: '#4285F4' }} />
                                <div>
                                    <h5 className="fw-bold mb-0">Google Reviews</h5>
                                    <small className="text-muted">Based on {googleRating.reviews} reviews</small>
                                </div>
                            </div>

                            <div className="d-flex align-items-center mb-3">
                                <div className="me-3">
                                    <h2 className="fw-bold mb-0">{googleRating.rating}</h2>
                                </div>
                                <div>
                                    <div className="d-flex mb-1">
                                        {[...Array(googleRating.stars)].map((_, i) => (
                                            <FaStar key={i} className="me-1" style={{ color: '#FFD700' }} />
                                        ))}
                                    </div>
                                    <small className="text-muted">Excellent rating</small>
                                </div>
                            </div>

                            <button
                                className="btn btn-outline-primary w-100"
                                style={{ borderRadius: '50px' }}
                            >
                                <FaGoogle className="me-2" />
                                View All Reviews
                            </button>
                        </Card.Body>
                    </Card>
                </motion.div>

                {/* Featured Testimonials with Real Photos */}
                <Row className="g-4 mb-5">
                    {testimonials.slice(0, 3).map((testimonial, index) => (
                        <Col lg={4} md={6} key={index}>
                            <motion.div
                                initial={{ opacity: 0, y: 20 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.5, delay: index * 0.1 }}
                                viewport={{ once: true }}
                                whileHover={{ y: -10 }}
                            >
                                <Card className="healthcare-card border-0 h-100">
                                    <Card.Body className="p-4">
                                        <div className="d-flex align-items-center mb-4">
                                            <div
                                                className="rounded-circle overflow-hidden me-3"
                                                style={{
                                                    width: '70px',
                                                    height: '70px',
                                                    flexShrink: 0
                                                }}
                                            >
                                                <img
                                                    src={testimonial.image}
                                                    alt={testimonial.name}
                                                    style={{
                                                        width: '100%',
                                                        height: '100%',
                                                        objectFit: 'cover'
                                                    }}
                                                    onError={(e) => {
                                                        e.target.src = `https://ui-avatars.com/api/?name=${testimonial.name.replace(' ', '+')}&background=20B2AA&color=fff&size=70&bold=true`;
                                                    }}
                                                />
                                            </div>
                                            <div>
                                                <h6 className="fw-bold mb-1">{testimonial.name}</h6>
                                                <small className="text-muted">{testimonial.role}</small>
                                            </div>
                                        </div>

                                        <FaQuoteLeft className="mb-3" style={{ color: 'var(--primary-teal)', opacity: 0.5 }} />

                                        <p style={{ color: '#666', fontStyle: 'italic', lineHeight: '1.6' }}>
                                            "{testimonial.content}"
                                        </p>

                                        <div className="mt-4 pt-3 border-top">
                                            <div className="d-flex justify-content-between align-items-center">
                                                <div className="d-flex">
                                                    {[...Array(testimonial.rating)].map((_, i) => (
                                                        <FaStar key={i} size={14} className="me-1" style={{ color: '#FFD700' }} />
                                                    ))}
                                                </div>
                                                <small className="text-muted">{testimonial.date}</small>
                                            </div>
                                        </div>
                                    </Card.Body>
                                </Card>
                            </motion.div>
                        </Col>
                    ))}
                </Row>

                {/* Auto-Rotating Testimonial Carousel */}
                <motion.div
                    initial={{ opacity: 0 }}
                    whileInView={{ opacity: 1 }}
                    transition={{ duration: 0.8 }}
                    viewport={{ once: true }}
                    className="mt-5"
                >
                    <div className="position-relative">
                        <div className="text-center mb-4">
                            <h4 className="fw-bold" style={{ color: 'var(--dark-teal)' }}>
                                More Patient Stories
                            </h4>
                            <small className="text-muted">Stories rotate automatically every 5 seconds</small>
                        </div>

                        <AnimatePresence mode="wait">
                            <motion.div
                                key={activeIndex}
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                exit={{ opacity: 0, y: -20 }}
                                transition={{ duration: 0.5 }}
                                className="text-center"
                            >
                                <Card className="border-0 healthcare-card" style={{ maxWidth: '800px', margin: '0 auto' }}>
                                    <Card.Body className="p-5">
                                        <div className="row align-items-center">
                                            <Col md={4} className="mb-4 mb-md-0">
                                                <div className="rounded-circle overflow-hidden mx-auto"
                                                    style={{
                                                        width: '150px',
                                                        height: '150px'
                                                    }}
                                                >
                                                    <img
                                                        src={testimonials[activeIndex].image}
                                                        alt={testimonials[activeIndex].name}
                                                        style={{
                                                            width: '100%',
                                                            height: '100%',
                                                            objectFit: 'cover'
                                                        }}
                                                        onError={(e) => {
                                                            e.target.src = `https://ui-avatars.com/api/?name=${testimonials[activeIndex].name.replace(' ', '+')}&background=20B2AA&color=fff&size=150&bold=true`;
                                                        }}
                                                    />
                                                </div>

                                                <h5 className="fw-bold mt-3 mb-2">{testimonials[activeIndex].name}</h5>
                                                <p className="text-muted mb-0">{testimonials[activeIndex].role}</p>

                                                <div className="d-flex justify-content-center mt-2">
                                                    {[...Array(testimonials[activeIndex].rating)].map((_, i) => (
                                                        <FaStar key={i} className="mx-1" style={{ color: '#FFD700' }} />
                                                    ))}
                                                </div>
                                            </Col>

                                            <Col md={8}>
                                                <FaQuoteLeft size={30} className="mb-3" style={{ color: 'var(--primary-teal)', opacity: 0.3 }} />

                                                <blockquote className="mb-4" style={{
                                                    fontSize: '1.1rem',
                                                    color: '#555',
                                                    lineHeight: '1.8',
                                                    fontStyle: 'italic'
                                                }}>
                                                    "{testimonials[activeIndex].content}"
                                                </blockquote>

                                                <div className="d-flex align-items-center justify-content-between mt-4 pt-3 border-top">
                                                    <small className="text-muted">{testimonials[activeIndex].date}</small>
                                                    <div className="d-flex align-items-center">
                                                        <div className="spinner-grow spinner-grow-sm text-primary me-2" role="status">
                                                            <span className="visually-hidden">Auto-playing</span>
                                                        </div>
                                                        <small className="text-muted">Auto-playing</small>
                                                    </div>
                                                </div>
                                            </Col>
                                        </div>
                                    </Card.Body>
                                </Card>
                            </motion.div>
                        </AnimatePresence>

                        {/* Navigation Dots - All Testimonials */}
                        <div className="d-flex justify-content-center align-items-center mt-4">
                            <div className="d-flex flex-wrap justify-content-center gap-2">
                                {testimonials.map((_, index) => (
                                    <button
                                        key={index}
                                        onClick={() => setActiveIndex(index)}
                                        className="btn p-0 position-relative"
                                        style={{
                                            width: '14px',
                                            height: '14px',
                                            borderRadius: '50%',
                                            backgroundColor: activeIndex === index ? 'var(--primary-teal)' : '#ddd',
                                            border: 'none',
                                            transition: 'all 0.3s ease',
                                            overflow: 'hidden'
                                        }}
                                        aria-label={`Go to testimonial ${index + 1}`}
                                    >
                                        {activeIndex === index && (
                                            <motion.div
                                                className="position-absolute top-0 left-0 h-100 bg-primary-teal"
                                                style={{ width: '0%', backgroundColor: 'var(--primary-teal)' }}
                                                animate={{ width: '100%' }}
                                                transition={{ duration: 5, ease: "linear" }}
                                            />
                                        )}
                                    </button>
                                ))}
                            </div>
                        </div>

                        {/* Manual Navigation Buttons */}
                        <div className="d-flex justify-content-center mt-3 gap-3">
                            <button
                                onClick={() => setActiveIndex(prev => prev === 0 ? testimonials.length - 1 : prev - 1)}
                                className="btn btn-outline-primary btn-sm"
                                style={{ borderRadius: '20px', padding: '5px 15px' }}
                            >
                                Previous
                            </button>
                            <button
                                onClick={() => setActiveIndex(prev => prev === testimonials.length - 1 ? 0 : prev + 1)}
                                className="btn btn-primary-teal btn-sm"
                                style={{ borderRadius: '20px', padding: '5px 15px' }}
                            >
                                Next
                            </button>
                        </div>
                    </div>
                </motion.div>

                {/* Video Testimonials Callout */}
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.8, delay: 0.2 }}
                    viewport={{ once: true }}
                    className="mt-5 pt-5"
                >
                    <div
                        className="p-5 rounded text-center healthcare-card"
                        style={{
                            backgroundColor: 'var(--soft-yellow)',
                            border: '2px solid var(--primary-teal)'
                        }}
                    >
                        <FaPlayCircle size={50} className="mb-4" style={{ color: 'var(--primary-teal)' }} />
                        <h4 className="fw-bold mb-3" style={{ color: 'var(--dark-teal)' }}>
                            Watch Patient Stories
                        </h4>
                        <p className="mb-4" style={{
                            maxWidth: '600px',
                            margin: '0 auto',
                            color: '#666',
                            fontSize: '1.1rem',
                            lineHeight: '1.6'
                        }}>
                            See and hear from our patients about their healthcare journeys in their own words
                        </p>
                        <div className="d-flex flex-wrap justify-content-center gap-3">
                            <button
                                className="btn-sky-blue px-4 py-2"
                                style={{ borderRadius: '50px', fontWeight: '600' }}
                            >
                                View Video Testimonials
                            </button>
                            <button
                                className="btn btn-outline-primary px-4 py-2"
                                style={{ borderRadius: '50px', fontWeight: '600' }}
                            >
                                Share Your Story
                            </button>
                        </div>
                    </div>
                </motion.div>
            </Container>
        </section>
    );
};

export default TestimonialsSection;